// Environment configuration for Reqqy

const isBrowser = typeof window !== "undefined";

export const isLocalhost =
  isBrowser &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const trimEnv = (value: any): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const env = import.meta.env;

// Local overrides are intentionally separate from production env vars so a local
// test cannot accidentally point at production services on localhost.
const localBackendUrl =
  trimEnv(env.VITE_LOCAL_BACKEND_URL) ??
  "http://localhost:8000";

const localSupabaseUrl =
  trimEnv(env.VITE_LOCAL_SUPABASE_URL) ??
  "http://127.0.0.1:8160";

// Supabase CLI often prints this as an anon key. Reqqy calls it publishable key
// in the app because the hosted project uses the newer publishable-key naming.
const localSupabasePublishableKey =
  trimEnv(env.VITE_LOCAL_SUPABASE_PUBLISHABLE_KEY) ??
  trimEnv(env.VITE_LOCAL_SUPABASE_ANON_KEY);

const productionBackendUrl =
  trimEnv(env.VITE_BACKEND_URL) ?? "https://reqqy-backend.onrender.com";

const useWorkspaceApi =
  env.VITE_USE_WORKSPACE_API === "true" ||
  env.VITE_FORCE_WORKSPACE_API === "true";

export const config = {
  backendUrl: isLocalhost ? localBackendUrl : productionBackendUrl,

  apiBaseUrl: isLocalhost
    ? `${localBackendUrl}/api`
    : `${productionBackendUrl}/api`,

  workspaceApiBaseUrl: isLocalhost
    ? `${localBackendUrl}/api/workspace`
    : `${productionBackendUrl}/api/workspace`,

  supabaseUrl: isLocalhost
    ? localSupabaseUrl
    : trimEnv(env.VITE_SUPABASE_URL),

  supabasePublishableKey: isLocalhost
    ? localSupabasePublishableKey
    : trimEnv(env.VITE_SUPABASE_PUBLISHABLE_KEY),

  wsProtocol: trimEnv(env.VITE_WS_PROTOCOL) || "ws",
  env: env.MODE || "development",
  isLocalhost,

  /**
   * Legacy direct workspace API mode.
   * Keep this opt-in so localhost still follows the production architecture:
   * frontend → local Supabase/Edge Functions → local backend compute.
   */
  useLocalBackend: useWorkspaceApi,

  /** Set to true to enable the geo-localized cookie consent banner & GA tracking */
  enableCookieConsent: false,

  /**
   * Frontend-only demo mode should only ever activate on localhost.
   * This prevents a committed VITE_REQQY_DEMO_MODE value from affecting production.
   */
  demoMode: isLocalhost && env.VITE_REQQY_DEMO_MODE === "true",
} as const;

export const getWsUrl = (path: string): string => {
  const baseUrl = config.backendUrl.replace(/^http/, config.wsProtocol);
  return `${baseUrl}${path}`;
};

export const getSupabaseFunctionUrl = (functionName: string, suffix = ""): string => {
  const normalizedSuffix = suffix ? (suffix.startsWith("?") || suffix.startsWith("/") ? suffix : `/${suffix}`) : "";
  return `${config.supabaseUrl}/functions/v1/${functionName}${normalizedSuffix}`;
};

export const supabaseFunctionHeaders = (): Record<string, string> => ({
  apikey: config.supabasePublishableKey || "",
});
